import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

// Cache for effective timezone
let cachedTimezone: string | null = null;
let lastUserId: string | null = null;
let lastOrgId: string | null = null;

/**
 * Clear the timezone cache - useful when organization settings change
 */
export function clearTimezoneCache(): void {
  cachedTimezone = null;
  lastUserId = null;
  lastOrgId = null;
}

/**
 * Get the effective timezone for the current user
 *
 * TIMEZONE HIERARCHY:
 * The user.timezone field in the database is intentionally left NULL when
 * timezone_preference is 'system' or 'organization'. This allows the hierarchy
 * to work properly:
 *
 * - 'system': Uses browser-detected timezone (dayjs.tz.guess())
 * - 'organization': Uses org timezone, falls back to browser timezone
 * - 'custom': Uses user.timezone (populated), falls back to org/browser
 *
 * This design ensures the frontend always gets the correct effective timezone
 * even when the database field is NULL.
 *
 * IMPORTANT: The backend has an equivalent function in backend/src/utils/timezoneUtils.ts
 * (TimezoneUtils.getEffectiveTimezone). Keep both in sync when making changes!
 *
 * @param user - Current user object
 * @param orgSettings - Organization settings object
 */
export function getEffectiveTimezone(
  user?: { 
    id?: string; 
    timezonePreference?: 'system' | 'organization' | 'custom';
    timezone?: string | null;
    currentOrganizationId?: string;
  } | null,
  orgSettings?: { timezone?: string } | null
): string {
  try {
    const currentUserId = user?.id || null;
    const currentOrgId = user?.currentOrganizationId || null;

    // Check if cache is valid
    if (
      cachedTimezone &&
      lastUserId === currentUserId &&
      lastOrgId === currentOrgId
    ) {
      return cachedTimezone;
    }

    // Resolve timezone based on user preference
    const timezonePreference = user?.timezonePreference || 'system';
    const userTimezone = user?.timezone;
    const orgTimezone = orgSettings?.timezone;
    const browserTimezone = dayjs.tz.guess();

    let resolvedTimezone: string;

    switch (timezonePreference) {
      case 'system':
        resolvedTimezone = browserTimezone;
        break;
      case 'organization':
        resolvedTimezone = orgTimezone || browserTimezone;
        break;
      case 'custom':
        resolvedTimezone = userTimezone || orgTimezone || browserTimezone;
        break;
      default:
        // Fallback for undefined/null preference
        resolvedTimezone = orgTimezone || browserTimezone;
    }

    // Validate the resolved timezone
    if (resolvedTimezone && isValidTimezone(resolvedTimezone)) {
      cachedTimezone = resolvedTimezone;
    } else {
      // Fallback to browser timezone if resolved timezone is invalid
      cachedTimezone = browserTimezone;
    }

    lastUserId = currentUserId;
    lastOrgId = currentOrgId;

    return cachedTimezone;
  } catch (error) {
    console.error('Error resolving timezone:', error);
    return dayjs.tz.guess();
  }
}

/**
 * Format a date/time string in the user's effective timezone
 * @param dateTime - ISO date string or null
 * @param format - dayjs format string (default: 'h:mm A')
 * @param showTimezone - whether to append timezone abbreviation (default: true)
 * @param user - Current user object
 * @param orgSettings - Organization settings object
 * @returns Formatted time string with optional timezone
 */
export function formatInUserTimezone(
  dateTime: string | null,
  format: string = 'h:mm A',
  showTimezone: boolean = true,
  user?: { 
    id?: string; 
    timezonePreference?: 'system' | 'organization' | 'custom';
    timezone?: string | null;
    currentOrganizationId?: string;
  } | null,
  orgSettings?: { timezone?: string } | null
): string {
  if (!dateTime) return 'TBD';

  try {
    const tz = getEffectiveTimezone(user, orgSettings);
    const formatted = dayjs(dateTime).tz(tz).format(format);

    if (showTimezone) {
      const tzAbbr = getTimezoneAbbreviation(user, orgSettings, dateTime);
      return `${formatted} ${tzAbbr}`;
    }

    return formatted;
  } catch (error) {
    console.error('Error formatting time in user timezone:', error);
    return 'TBD';
  }
}

/**
 * Get timezone abbreviation for the effective timezone
 * @param user - Current user object
 * @param orgSettings - Organization settings object
 * @param date - Specific date to get abbreviation for (defaults to current date)
 * @returns Timezone abbreviation (e.g., 'EST', 'EDT', 'PST', 'PDT')
 */
export function getTimezoneAbbreviation(
  user?: { 
    id?: string; 
    timezonePreference?: 'system' | 'organization' | 'custom';
    timezone?: string | null;
    currentOrganizationId?: string;
  } | null,
  orgSettings?: { timezone?: string } | null,
  date?: string | Date
): string {
  try {
    const tz = getEffectiveTimezone(user, orgSettings);
    // Use the specific date to get the correct abbreviation (EDT vs EST, etc.)
    const referenceDate = date ? dayjs(date) : dayjs();
    return referenceDate.tz(tz).format('z');
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return 'UTC';
  }
}

/**
 * Invalidate the timezone cache (call when user/org changes)
 */
export function invalidateTimezoneCache(): void {
  cachedTimezone = null;
  lastUserId = null;
  lastOrgId = null;
}

/**
 * Convert a date/time to the user's effective timezone
 * @param dateTime - ISO date string
 * @param user - Current user object
 * @param orgSettings - Organization settings object
 * @returns dayjs object in user's timezone
 */
export function toUserTimezone(
  dateTime: string,
  user?: { 
    id?: string; 
    timezonePreference?: 'system' | 'organization' | 'custom';
    timezone?: string | null;
    currentOrganizationId?: string;
  } | null,
  orgSettings?: { timezone?: string } | null
): Dayjs {
  try {
    const tz = getEffectiveTimezone(user, orgSettings);
    return dayjs(dateTime).tz(tz);
  } catch (error) {
    console.error('Error converting to user timezone:', error);
    return dayjs(dateTime);
  }
}

/**
 * Validate if a timezone string is valid
 * @param timezone - timezone string to validate
 * @returns true if valid, false otherwise
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    dayjs().tz(timezone);
    return true;
  } catch {
    return false;
  }
}
